import { Inject, Injectable, PLATFORM_ID, effect, inject, signal, untracked } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { CustomerAddress } from './address.models';
import { CustomerAuthService } from './customer-auth.service';

/**
 * Where a chosen spot came from — persisted with it, because it decides what survives a sign-out.
 * 'city' is a tap on a popular city (not personal). 'address' is a row from the customer's book and
 * 'pin' is a searched / located / dragged spot, whose label is the reverse-geocoded STREET — both are
 * the customer's own data and must not outlive their session on a shared device.
 */
export type LocationSource = 'address' | 'pin' | 'city';

export interface OrderLocation {
  label: string;
  lat: number;
  lng: number;
  /** Provenance of this spot. Absent only on payloads written before this field existed. */
  source?: LocationSource;
}

export interface PopularCity {
  name: string;
  lat: number;
  lng: number;
}

const STORAGE_KEY = 'orderyaa.location.v1';

/** The saved address the customer picked, kept next to the location so a reload (or opening
 *  /checkout in a new tab) still has the CRM row — its id is what links the order to the address
 *  book, and its city/state/postcode travel with the order. */
const ADDRESS_KEY = 'orderyaa.address.v1';

/** Retired key. Home/Work/Other used to be mirrored into localStorage so the picker could offer
 *  quick chips without a store context. That cache outlived the session — the next person on the
 *  device saw the previous customer's addresses — and it is no longer needed: the picker now reads
 *  the real address book from the API with or without a store. Cleared on boot. */
const RETIRED_PLACES_KEY = 'orderyaa.places.v1';

/**
 * The shopper's chosen delivery location, shared across the storefront. Set via the location
 * picker modal (Locate me / search / popular city / map pin / a saved address) and persisted to
 * localStorage. Also owns the picker's open state so any component (header, empty states) can open it.
 *
 * Anything that came from the signed-in customer's address book is session data: signing out clears
 * it, so the next person on this device never sees the previous customer's home address.
 */
@Injectable({ providedIn: 'root' })
export class LocationService {
  private readonly auth = inject(CustomerAuthService);
  private readonly isBrowser: boolean;
  private readonly _location = signal<OrderLocation | null>(null);
  private readonly _pickerOpen = signal(false);
  /** The saved address the customer chose in the picker (null = a raw map pin / city). */
  private readonly _selectedAddress = signal<CustomerAddress | null>(null);

  readonly location = this._location.asReadonly();
  readonly pickerOpen = this._pickerOpen.asReadonly();
  readonly selectedAddress = this._selectedAddress.asReadonly();

  readonly popular: PopularCity[] = [
    { name: 'Karachi', lat: 24.8607, lng: 67.0011 },
    { name: 'Lahore', lat: 31.5204, lng: 74.3587 },
    { name: 'Rawalpindi', lat: 33.5651, lng: 73.0169 },
    { name: 'Islamabad', lat: 33.6844, lng: 73.0479 },
    { name: 'Multan', lat: 30.1575, lng: 71.5249 },
  ];

  /** Whether this visit has seen a signed-in session, so the sign-out teardown fires on the
   *  TRANSITION only — a guest's own location must survive an ordinary page load. */
  private sawSignedIn = false;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      this._location.set(this.read<OrderLocation>(STORAGE_KEY));
      this._selectedAddress.set(this.read<CustomerAddress>(ADDRESS_KEY));
      try {
        localStorage.removeItem(RETIRED_PLACES_KEY);
      } catch {
        /* ignore */
      }
      if (!this.auth.isAuthenticated()) {
        // A signed-out visitor has no address book, so a saved row lingering here can only be the
        // previous customer's — drop it whatever else happens below.
        this._selectedAddress.set(null);
        this.persist(ADDRESS_KEY, null);
        // One-time sweep of location payloads written before `source` existed: on a signed-out device
        // there is no way to tell a guest's city pick from a departed customer's street, so drop it
        // rather than keep showing "Deliver to <someone's street>". Everything written from now on
        // carries its source.
        if (this._location() && !this._location()!.source) this.forgetCustomerLocation();
      }
    }
    // Teardown on the signed-in → signed-out transition. Sits here rather than in the sign-out
    // handlers so a session the interceptor tears down (dead refresh token) clears this too.
    effect(() => {
      const signedIn = this.auth.isAuthenticated();
      untracked(() => {
        if (signedIn) {
          this.sawSignedIn = true;
        } else if (this.sawSignedIn) {
          this.sawSignedIn = false;
          this.forgetCustomerLocation();
        }
      });
    });
  }

  private read<T>(key: string): T | null {
    try {
      const raw = localStorage.getItem(key);
      return raw ? (JSON.parse(raw) as T) : null;
    } catch {
      return null;
    }
  }

  /** True when the picker was opened to choose where to shop (header / stores) — confirming should
   *  take the shopper to the stores list. False when picking a delivery address mid-checkout. */
  private _navigateOnConfirm = false;
  get navigateOnConfirm(): boolean {
    return this._navigateOnConfirm;
  }

  openPicker(navigateToStoresOnConfirm = false): void {
    this._navigateOnConfirm = navigateToStoresOnConfirm;
    this._pickerOpen.set(true);
  }

  closePicker(): void {
    this._pickerOpen.set(false);
  }

  setLocation(loc: OrderLocation): void {
    this._location.set(loc);
    this.persistLocation(loc);
  }

  /** Record the saved address chosen in the picker (or null when a raw pin/city was picked). */
  setSelectedAddress(address: CustomerAddress | null): void {
    this._selectedAddress.set(address);
    this.persist(ADDRESS_KEY, address);
  }

  private persistLocation(loc: OrderLocation | null): void {
    this.persist(STORAGE_KEY, loc);
  }

  private persist(key: string, value: unknown | null): void {
    if (!this.isBrowser) return;
    try {
      if (value) localStorage.setItem(key, JSON.stringify(value));
      else localStorage.removeItem(key);
    } catch {
      /* ignore */
    }
  }

  /**
   * Forget everything that belonged to the customer: the selected saved address, and the chosen spot
   * itself unless it was a popular-city tap. An address row and a located/searched/dragged pin both
   * carry the customer's real street in their label, which the header shows as "Deliver to …" — that
   * must not greet the next person on the device. A city is kept: it isn't personal, and dropping it
   * would just make a returning shopper pick their city again.
   */
  private forgetCustomerLocation(): void {
    this._selectedAddress.set(null);
    this.persist(ADDRESS_KEY, null);
    if (this._location() && this._location()!.source !== 'city') {
      this._location.set(null);
      this.persistLocation(null);
    }
  }
}
